import { cva, type VariantProps } from 'class-variance-authority';

/**
 * LinearGauge styling. Segment categories read the `data-category` tokens, set
 * by the tenant's Theme v2 `colors.data` roles and otherwise the brand scales
 * (bar/dot = base, halo = `-subtle`). The pill reads `colors.surface.base`.
 */

/** Root: fills its container by default so authors need no className. */
export const linearGaugeVariants = cva('w-full');

/** A single segment bar: 4px track that fills its grid column. */
export const linearGaugeSegmentVariants = cva('h-1 w-full rounded-xs', {
  variants: {
    category: {
      1: 'bg-data-category-1',
      2: 'bg-data-category-2',
      3: 'bg-data-category-3',
    },
  },
  defaultVariants: {
    category: 1,
  },
});

/**
 * Indicator dot (10px) with a ~5px halo ring. Dot uses the occupied segment's
 * `data-category` color; the halo uses that category's `-subtle` step.
 */
export const linearGaugeIndicatorVariants = cva(
  'pointer-events-none absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-[5px]',
  {
    variants: {
      category: {
        1: 'bg-data-category-1 ring-data-category-1-subtle',
        2: 'bg-data-category-2 ring-data-category-2-subtle',
        3: 'bg-data-category-3 ring-data-category-3-subtle',
      },
    },
    defaultVariants: {
      category: 1,
    },
  },
);

/**
 * ValueMarker pill, offset so its caret (`md` from the `caretSide` end) meets the dot.
 * The min width keeps short values from pulling the rounded corner under the caret.
 */
export const linearGaugeValueMarkerVariants = cva(
  'relative mb-md flex w-max min-w-[calc(2*(theme(spacing.md)_+_theme(borderWidth.DEFAULT)))] items-center justify-center whitespace-nowrap rounded-max border bg-surface px-xs py-min',
  {
    variants: {
      bordered: {
        true: 'border-border-divider shadow-sm',
        false: 'border-transparent',
      },
      caretSide: {
        start:
          'translate-x-[calc(-1*theme(spacing.md)_-_theme(borderWidth.DEFAULT))]',
        end: 'translate-x-[calc(-100%_+_theme(spacing.md)_+_theme(borderWidth.DEFAULT))]',
      },
    },
    defaultVariants: {
      bordered: false,
      caretSide: 'start',
    },
  },
);

/** Downward caret triangle, covering the pill's bottom border where they meet. */
export const linearGaugeValueMarkerCaretVariants = cva(
  'absolute top-full h-xxs w-xs overflow-visible fill-surface',
  {
    variants: {
      bordered: {
        true: 'stroke-border-divider',
        false: '',
      },
      caretSide: {
        start: 'left-md -translate-x-1/2',
        end: 'right-md translate-x-1/2',
      },
    },
    defaultVariants: {
      bordered: false,
      caretSide: 'start',
    },
  },
);

export type LinearGaugeSegmentVariantProps = VariantProps<
  typeof linearGaugeSegmentVariants
>;
